import React, { useEffect, useRef, useState } from "react";
import { ToolbarAvoid } from "@autodromo/contextual-fields";
import { afField } from "@autodromo/contextual-fields";
import { formatGtagLabel } from "../../../utils/formatGtagLabel";
import type { DynamicStructure } from "../../../utils/interpolate";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import { getLinkUrl, linkKey, resolveMegaMenuCards, resolveMenuItemMode, shouldOpenInNewTab } from "../logic";
import { STRINGS } from "../strings";
import { Back, Check, Chevron, Close } from "./Icons";
import { MegaMenuGrid } from "./MegaMenuGrid";
import { MegaMenuSeeAllLink } from "./MegaMenuSeeAllLink";
import { DesktopButton } from "./ActionButtons";
import { SubmenuItems } from "./SubmenuItems";
import {
  Drawer,
  DrawerBackdrop,
  DrawerButtons,
  DrawerClose,
  DrawerContacts,
  DrawerFooter,
  DrawerHeader,
  DrawerItem,
  DrawerItemButton,
  DrawerItemLink,
  DrawerList,
  DrawerPanel,
  DrawerBack,
  DrawerSubHeader,
  DrawerSubLink,
  DrawerSubList,
  DrawerSubPanel,
  DrawerSubTitle,
  DrawerTopSlot,
  DrawerUnitItem,
  DrawerUnitsButton,
} from "../HeaderV2.styles";
import type { UnitsSelectorState } from "../../../components/UnitsSelector/UnitsSelector";
import {
  MegaMenuCardLayout,
  MenuItemMode,
  MobileDividers,
  MobileDrawerSide,
  MobileMenuMode,
  type HeaderV2Button,
  type HeaderV2Link,
  type MegaMenuAspect,
  type MegaMenuCardAlign,
  type MegaMenuData,
} from "../types";

const UNITS_SUB_ID = "__units__";

type RenderUnits = (
  render: (params: { state: UnitsSelectorState; icon: React.ReactNode; showAllOption: boolean }) => React.ReactNode,
) => React.ReactNode;

type MobileDrawerProps = {
  open: boolean;
  mode: MobileMenuMode;
  backdrop: boolean;
  onClose: () => void;
  logo: React.ReactNode;
  links: HeaderV2Link[];
  buttons: HeaderV2Button[];
  contacts?: React.ReactNode;
  renderUnits?: RenderUnits;
  htmlId?: string;
  megaMenuData?: MegaMenuData;
  structure?: DynamicStructure;
  aspect: MegaMenuAspect;
  cardLayout?: MegaMenuCardLayout;
  cardAlign?: MegaMenuCardAlign;
  side?: MobileDrawerSide;
  dividers?: MobileDividers;
};

export const MobileDrawer = ({
  open,
  mode,
  backdrop,
  onClose,
  logo,
  links,
  buttons,
  contacts,
  renderUnits,
  htmlId,
  megaMenuData,
  structure,
  aspect,
  cardLayout,
  cardAlign,
  side = MobileDrawerSide.RIGHT,
  dividers = MobileDividers.LINES,
}: MobileDrawerProps) => {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const [openSubId, setOpenSubId] = useState<string | null>(null);

  useFocusTrap({ containerRef: drawerRef, active: open });

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const listLayout = cardLayout ?? MegaMenuCardLayout.ABOVE;

  const openLink = openSubId ? links.find((link, index) => linkKey({ link, index }) === openSubId) : undefined;
  const openLinkMode = openLink ? resolveMenuItemMode({ link: openLink, megaMenuData }) : null;
  const openLinkData = openSubId ? megaMenuData?.[openSubId] : undefined;

  return (
    <>
      <DrawerBackdrop
        type="button"
        $visible={backdrop || mode === MobileMenuMode.FULLSCREEN}
        aria-label={STRINGS.closeMenuBackdrop}
        tabIndex={-1}
        onClick={onClose}
      />
      <Drawer
        ref={drawerRef}
        $mode={mode}
        $side={side}
        data-toolbar-avoid={ToolbarAvoid.CONTENT}
        role="dialog"
        aria-modal="true"
        aria-label={STRINGS.mainNavigation}
        data-testid="header-v2-drawer"
      >
        <DrawerHeader>
          {logo}
          <DrawerClose type="button" onClick={onClose} aria-label={STRINGS.closeMenu} data-toolbar-avoid={ToolbarAvoid.ACTION}>
            <Close />
          </DrawerClose>
        </DrawerHeader>

        {renderUnits?.(({ state, icon }) => (
          <DrawerTopSlot>
            <DrawerUnitsButton
              type="button"
              data-testid="header-v2-drawer-units-strip"
              data-af-field="showUnitsSelector"
              onClick={() => setOpenSubId(UNITS_SUB_ID)}
              aria-expanded={openSubId === UNITS_SUB_ID}
              title={state.displayText}
            >
              <span className="hv2-units-value">
                {icon && <span className="hv2-units-icon">{icon}</span>}
                <span className="hv2-units-name">{state.displayText}</span>
              </span>
              <Chevron size={14} className="hv2-chevron-right" />
            </DrawerUnitsButton>
          </DrawerTopSlot>
        ))}

        <DrawerList>
          {links.map((link, index) => {
            const id = linkKey({ link, index });
            const itemMode = resolveMenuItemMode({ link, megaMenuData });
            const gtag = {
              "data-gtag-action": "Clique Menu",
              "data-gtag-label": formatGtagLabel(htmlId, link.name),
              "data-gtag-category": "Cabeçalho",
            };

            if (itemMode === MenuItemMode.LINK) {
              return (
                <DrawerItem key={id} $dividers={dividers} data-af-field={afField("links", link.id)}>
                  <DrawerItemLink
                    href={getLinkUrl({ item: link })}
                    target={shouldOpenInNewTab({ item: link }) ? "_blank" : "_self"}
                    data-af-field={afField("links", link.id, "name")}
                    {...gtag}
                  >
                    {link.name}
                  </DrawerItemLink>
                </DrawerItem>
              );
            }

            return (
              <DrawerItem key={id} $dividers={dividers} data-af-field={afField("links", link.id)}>
                <DrawerItemButton
                  type="button"
                  aria-expanded={false}
                  onClick={() => setOpenSubId(id)}
                  data-af-field={afField("links", link.id, "name")}
                  {...gtag}
                >
                  {link.name}
                  <Chevron size={14} className="hv2-chevron-right" />
                </DrawerItemButton>
              </DrawerItem>
            );
          })}
        </DrawerList>

        {openSubId === UNITS_SUB_ID &&
          renderUnits?.(({ state, showAllOption }) => (
            <DrawerSubPanel $side={side} data-testid="header-v2-drawer-units">
              <DrawerSubHeader>
                <DrawerBack
                  type="button"
                  onClick={() => setOpenSubId(null)}
                  aria-label={STRINGS.backToMenu}
                  data-toolbar-avoid={ToolbarAvoid.ACTION}
                >
                  <Back />
                </DrawerBack>
                <DrawerSubTitle>{STRINGS.unitsPanelTitle}</DrawerSubTitle>
              </DrawerSubHeader>

              <DrawerSubList $dividers={dividers}>
                {showAllOption && (
                  <li>
                    <DrawerUnitItem
                      type="button"
                      title={state.allUnitsLabel}
                      $selected={state.isAllSelected}
                      onClick={() => {
                        state.onSelectAll();
                        setOpenSubId(null);
                      }}
                    >
                      <span>{state.allUnitsLabel}</span>
                      {state.isAllSelected && <Check />}
                    </DrawerUnitItem>
                  </li>
                )}
                {state.options.map((option) => (
                  <li key={option.key}>
                    <DrawerUnitItem
                      type="button"
                      title={option.label}
                      $selected={option.isSelected}
                      onClick={() => {
                        option.onSelect();
                        setOpenSubId(null);
                      }}
                    >
                      <span>{option.label}</span>
                      {option.isSelected && <Check />}
                    </DrawerUnitItem>
                  </li>
                ))}
              </DrawerSubList>
            </DrawerSubPanel>
          ))}

        {openLink && openLinkMode && openLinkMode !== MenuItemMode.LINK && (
          <DrawerSubPanel $side={side} data-testid="header-v2-drawer-subpanel">
            <DrawerSubHeader>
              <DrawerBack
                type="button"
                onClick={() => setOpenSubId(null)}
                aria-label={STRINGS.backToMenu}
                data-toolbar-avoid={ToolbarAvoid.ACTION}
              >
                <Back />
              </DrawerBack>
              <DrawerSubTitle>{openLink.name}</DrawerSubTitle>
            </DrawerSubHeader>

            {openLinkMode === MenuItemMode.SUBMENU && (
              <DrawerSubList $dividers={dividers}>
                <SubmenuItems link={openLink} htmlId={htmlId} LinkComponent={DrawerSubLink} />
              </DrawerSubList>
            )}

            {openLinkMode === MenuItemMode.MEGAMENU && (
              <DrawerPanel>
                <MegaMenuGrid
                  status={openLinkData?.status}
                  cards={resolveMegaMenuCards({ entries: openLinkData?.entries ?? [], structure, item: openLink.megaMenu })}
                  columns={1}
                  aspect={aspect}
                  layout={listLayout}
                  cardAlign={cardAlign}
                  htmlId={htmlId}
                  skeletonCount={3}
                  onNavigate={onClose}
                />
                <MegaMenuSeeAllLink link={openLink} structure={structure} onNavigate={onClose} />
              </DrawerPanel>
            )}
          </DrawerSubPanel>
        )}

        {(buttons.length > 0 || contacts) && (
          <DrawerFooter data-toolbar-avoid={ToolbarAvoid.ACTION}>
            {buttons.length > 0 && (
              <DrawerButtons>
                {buttons.map((button, index) => (
                  <DesktopButton key={button.id ?? index} button={button} index={index} htmlId={htmlId} />
                ))}
              </DrawerButtons>
            )}
            {contacts && <DrawerContacts>{contacts}</DrawerContacts>}
          </DrawerFooter>
        )}
      </Drawer>
    </>
  );
};
